<template>
  <section class="cs-navigation" :class="[{ pinned: navigationIsPinned }]">
    <template v-if="navigationIsPinned">
      <div class="campsite-name">
        <pu-homepage-link exclude-type="homepageLink" :aria-label="$t('Home')">
          <img
            src="@/assets/images/pitchup-favicon.svg"
            alt=""
            width="36"
            height="32"
            class="logo"
          />
        </pu-homepage-link>
        {{ campsite.name }}
        <client-only>
          <campsite-favourite-button
            :campsite-id="campsite.id"
            :campsite-name="campsite.name"
            class="favourite-icon"
          />
        </client-only>
      </div>

      <span
        v-scrollto="{
          target: '#pitch-types',
          options: { duration: 200, offset: scrollOffset },
        }"
        class="header-meta"
        :class="[{ 'is-dated': isDated }]"
      >
        <template v-if="!isDated">
          <translate
            v-if="campsite.availableToSearch"
            class="check-availability-btn"
            >Check availability</translate
          >
          <span v-else class="zero-availability-msg">
            <img
              src="@/assets/images/warning.svg"
              width="15"
              height="15"
              alt=""
            />
            <translate> No availability at present on Pitchup.com </translate>
          </span>
        </template>
        <translate v-else-if="dateRange" class="check-availability-btn"
          >View prices & book</translate
        >

        <span v-if="!isDated && campsite.leadPrice" class="lead-price">
          <i18n-t keypath="From {price}">
            <template #price>
              <pu-price :price="campsite.leadPrice" />
            </template>
          </i18n-t>
          <translate
            class="leadprice-rules"
            :tt="{
              price: campsite.leadPriceNights,
              adultsCount: campsite.leadPriceAdults,
            }"
          >
            For {price} night, {adultsCount} adults
          </translate>
        </span>
      </span>
    </template>

    <nav class="cs-nav-bar">
      <a
        v-scrollto="{
          target: '#description',
          options: { duration: 200, offset: scrollOffset },
        }"
        href="#description"
        :class="['description', { selected: activeSection === 'description' }]"
        @click.prevent="handleNavClick('#description', true)"
      >
        <translate> Description </translate>
      </a>

      <a
        v-scrollto="{
          target: '#pitch-types',
          options: { duration: 200, offset: scrollOffset },
        }"
        href="#pitch-types"
        :class="['pitch-types', { selected: activeSection === 'pitch-types' }]"
        @click.prevent="handleNavClick('#pitch-types')"
      >
        <translate>Pitch types & availability</translate>
      </a>

      <a
        v-scrollto="{
          target: '#reviews',
          options: { duration: 200, offset: scrollOffset },
        }"
        href="#reviews"
        :class="['reviews', { selected: activeSection === 'reviews' }]"
        @click.prevent="handleNavClick('#reviews')"
      >
        <translate> Reviews </translate>
      </a>

      <a
        v-if="campsite.youtube"
        v-scrollto="{
          target: '#video',
          options: { duration: 200, offset: scrollOffset },
        }"
        href="#video"
        :class="['video', { selected: activeSection === 'video' }]"
        @click.prevent="handleNavClick('#video')"
      >
        <translate> Video </translate>
      </a>

      <a
        v-scrollto="{
          target: '#features',
          options: { duration: 200, offset: scrollOffset },
        }"
        href="#features"
        :class="['features', { selected: activeSection === 'features' }]"
        @click.prevent="handleNavClick('#features')"
      >
        <translate> Features </translate>
      </a>

      <a
        v-scrollto="{
          target: '#location',
          options: { duration: 200, offset: scrollOffset },
        }"
        href="#location"
        :class="['location', { selected: activeSection === 'location' }]"
        @click.prevent="handleNavClick('#location')"
      >
        <translate> Location </translate>
      </a>

      <a
        v-if="campsite.point"
        v-scrollto="{
          target: '#local-attractions',
          options: { duration: 200, offset: scrollOffset },
        }"
        href="#local-attractions"
        :class="[
          'local-attractions',
          { selected: activeSection === 'local-attractions' },
        ]"
        @click.prevent="handleNavClick('#local-attractions')"
      >
        <translate> Local attractions </translate>
      </a>

      <a
        v-if="campsite.isBookable"
        href="#policies"
        :class="[
          'policies',
          'booking-terms',
          { selected: activeSection === 'policies' },
        ]"
        @click="handleNavClick('#policies')"
      >
        <translate> Policies </translate>
      </a>
    </nav>
  </section>
</template>
<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue'
import type { CampsiteDetails } from '../types'
import type { Party } from './types'

type DateRange = { start: string; end: string }

const props = defineProps<{
  campsite: CampsiteDetails
  navigationOffscreen?: boolean
  isDated: boolean
  dateRange?: DateRange
  party?: Party
  categoryIds?: string[]
}>()

const emit = defineEmits<{
  (event: 'scroll-triggered', target: string): void
}>()

const scrollOffset = -75
const userHasScrolled = ref(false)
const activeSection = ref<string>('description')
const { $t } = useNuxtApp()

const navigationIsPinned = computed(
  () => !!props.navigationOffscreen && userHasScrolled.value,
)

let navigationObserver: IntersectionObserver | null = null
let observeRetryTimeout: ReturnType<typeof setTimeout> | null = null
const sectionIds = ref<string[]>([])
const visibleSections = new Set<string>()

// Track which subsection link should be highlighted based on visibility.
const updateActiveSection = () => {
  if (!sectionIds.value.length) return

  let nextActive = activeSection.value
  for (const id of sectionIds.value) {
    if (visibleSections.has(id)) {
      nextActive = id
    }
  }

  if (nextActive !== activeSection.value) {
    activeSection.value = nextActive
  }
}

const handleIntersections = (entries: IntersectionObserverEntry[]) => {
  entries.forEach((entry) => {
    const target = entry.target as HTMLElement
    const sectionId = target.id
    if (!sectionId) return

    if (entry.isIntersecting) {
      visibleSections.add(sectionId)
    } else {
      visibleSections.delete(sectionId)
    }
  })

  // Lazily flag when the visitor actually scrolled so we can pin the header.
  if (!userHasScrolled.value && typeof window !== 'undefined') {
    userHasScrolled.value = window.scrollY > 0
  }

  updateActiveSection()
}

const observeSections = () => {
  if (typeof window === 'undefined' || !('IntersectionObserver' in window)) {
    return
  }

  // Reset any previous run before bootstrapping a fresh observer cycle.
  if (observeRetryTimeout) {
    clearTimeout(observeRetryTimeout)
    observeRetryTimeout = null
  }

  navigationObserver?.disconnect()
  visibleSections.clear()

  const subsectionNodes = document.querySelectorAll<HTMLElement>(
    '[data-campsite-subsection]',
  )

  sectionIds.value = Array.from(subsectionNodes)
    .map((section) => section.id)
    .filter((id): id is string => Boolean(id))

  if (!sectionIds.value.length) {
    // Async subsection components often hydrate after we mount; retry shortly
    // so new nodes get registered without a full page refresh.
    observeRetryTimeout = setTimeout(() => {
      observeRetryTimeout = null
      observeSections()
    }, 100)
    return
  }

  if (!sectionIds.value.includes(activeSection.value)) {
    activeSection.value = sectionIds.value[0]
  }

  // Use a single observer instance to track visibility for every subsection.
  navigationObserver = new IntersectionObserver(handleIntersections, {
    rootMargin: '-25% 0px -75%',
  })

  subsectionNodes.forEach((section) => {
    if (section.id) {
      navigationObserver?.observe(section)
    }
  })
}

const updateScrollState = () => {
  if (typeof window === 'undefined') return
  userHasScrolled.value = window.scrollY > 0
}

const handleNavClick = (target: string, shouldEmit = false) => {
  if (shouldEmit) {
    emit('scroll-triggered', target)
  }

  const normalizedTarget = target.replace('#', '')
  if (normalizedTarget) {
    activeSection.value = normalizedTarget
  }
}

onMounted(() => {
  if (typeof window === 'undefined') return

  updateScrollState()
  window.addEventListener('scroll', updateScrollState, { passive: true })

  void nextTick(() => {
    observeSections()
  })
})

onBeforeUnmount(() => {
  if (typeof window !== 'undefined') {
    window.removeEventListener('scroll', updateScrollState)
  }

  // Ensure delayed retries are cancelled when the component is torn down.
  if (observeRetryTimeout) {
    clearTimeout(observeRetryTimeout)
    observeRetryTimeout = null
  }

  navigationObserver?.disconnect()
  navigationObserver = null
  visibleSections.clear()
})
</script>
<style scoped>
/* desktop only */
.cs-navigation {
  background: white;
  margin: auto;
  max-width: calc(var(--page-width-max));
  min-height: 2em;
  padding: 0 var(--page-width-padding);
  position: sticky;
  top: -1px;
  transition: all 0.25s ease-in-out;
  width: 100%;
}

.cs-navigation::after {
  content: '';
  position: absolute;
  inset: 0 0 0 0;
  opacity: 0;
  transition: opacity 0.4s ease;
  filter: drop-shadow(0 0.3em 0.25em rgba(0, 0, 0, 0.15));
  background: white;
  z-index: -1;
}

.cs-navigation.pinned::after {
  opacity: 1;
}

.logo {
  display: inline-block;
  height: 1em;
  vertical-align: baseline;
  width: auto;
}

.campsite-name {
  font-weight: bold;
  padding: 0.35em 0 0.5em;
  font-size: 1.3em;
  grid-row: 1/2;
  grid-column: 1/2;
}

.campsite-name a {
  display: inline-flex;
}

.favourite-icon.favourite-link :deep(svg) {
  margin-left: 0.2em;
  width: 1.2em;
}

.favourite-icon:not(.favourite) :deep(svg path) {
  stroke: var(--colour-dark-green);
}

.cs-navigation.pinned :deep(.favourite-add-form-modal .modal-trigger-wrapper) {
  padding: 0;
  background: transparent;
  border-radius: 0;
}

.header-meta {
  grid-column: 2/3;
  grid-row: 1 / 3;
  align-self: center;
  display: flex;
  column-gap: 0.8em;
  cursor: pointer;
}

.check-availability-btn {
  background: var(--colour-light-green);
  color: #fff;
  font-weight: bold;
  padding: 0.5em 0.8em;
  border-radius: 6em;
  cursor: pointer;
  margin: auto;
}

.check-availability-btn:hover,
.check-availability-btn:focus-visible {
  background: var(--colour-dark-green);
  outline: none;
}

.lead-price {
  text-align: right;
  color: var(--colour-header-light-blue);
  line-height: 1.2;
  display: block;
  font-size: 1.25em;
}

.leadprice-rules {
  white-space: nowrap;
  font-size: var(--small);
  color: var(--colour-text-light);
  display: block;
}

nav {
  align-items: center;
  display: flex;
  flex-direction: row;
  font-size: inherit;
  justify-content: flex-start;
  column-gap: 2em;
  max-width: calc(var(--page-width-max) - (2 * var(--page-width-padding)));
}

.cs-navigation.pinned nav {
  max-width: none;
  grid-row: 2/3;
  grid-column: 1/2;
  margin-right: 3em;
  padding-bottom: calc(0.5em - 2px);
  justify-content: space-between;
  column-gap: 0.5em;
}

.cs-navigation.pinned {
  display: grid;
  grid-template-columns: 1fr auto;
  grid-template-rows: auto auto;
  max-width: none;
  z-index: 3;
}

nav a {
  text-decoration: none;
  color: var(--colour-text-light);
  align-self: flex-start;
  text-align: center;
}

nav a span {
  position: relative;
  display: inline-block;
}

nav a.selected {
  color: var(--colour-dark-blue);
  font-weight: bold;
}

nav a.selected span:after,
nav a:hover span:after,
nav a:focus-visible span:after {
  content: '';
  height: 4px;
  width: 100%;
  position: absolute;
  border-radius: 1em;
  background: var(--colour-light-green);
  left: 0;
  bottom: -0.5em;
}

nav + span {
  cursor: pointer;
}

nav a:hover,
nav a:active,
nav a:focus-visible {
  color: var(--colour-dark-blue);
  outline: none;
}

.cs-navigation.pinned nav a {
  height: 100%;
  display: block;
}

.cs-navigation.pinned nav a span {
  height: 100%;
}

/* small tablets */
@media (max-width: 1200px) {
  .cs-navigation.pinned nav,
  .cs-nav-bar,
  .header-meta {
    font-size: var(--body);
  }
}

@media (max-width: 960px) {
  .header-meta {
    display: none;
  }

  .cs-navigation.pinned nav,
  .cs-nav-bar {
    margin-right: 0;
  }
}
</style>
